import { useEffect, useMemo, useState } from 'react';
import { useCombobox } from '@mantine/core';
import type { AxiosError } from 'axios';

import { CHURCH_TYPE, type Church, type ChurchCreateRequest, type ChurchUpdateRequest, ORGANIZATION_MODAL_MODE } from '@/types/churches';
import { BULLETIN_TYPE } from '@/types/enums';
import { STATES_DATA, TIMEZONES } from '@/config/countriesData';
import { churchesService } from '@/services/churches.service';
import {
  COORDINATE_TYPE,
  isCompleteUsPhoneNumber,
  isPhoneValueEmpty,
  toServerPhoneNumber,
  validateBulletinNumber,
  validateCoordinatePair,
} from '@/helpers';
import { transformCountryLabelToValue } from '@/shared/utils';
import type { ErrorResponse } from '@/types/error';
import type { MyParishBulletinUpdateRequest, MyParishView } from '@/types/myParish';
import { useComboboxSearch } from './useComboboxSearch';

export type OrganizationFormData = {
  fullName: string;
  shortName: string;
  type: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: string;
  longitude: string;
  dioceseId: string;
  phone: string;
  website: string;
  timezone: string;
  isClusterEditable: boolean;
  clusterEnabled: boolean;
  clusterName: string;
  keyword: string;
  qbAccount: string;
  discoverMassId: string;
  clusterOrganizations: ClusterOrganizationEntry[];
};

export type ClusterOrganizationEntry = {
  id: string;
  name: string;
  city: string;
  zipCode: string;
  productsEnabled: boolean;
};

const KEYWORD_MIN_LENGTH = 3;
const KEYWORD_MAX_LENGTH = 20;
const DISCOVER_MASS_ID_MIN_LENGTH = 4;
const DISCOVER_MASS_ID_MAX_LENGTH = 7;

const INITIAL_FORM_DATA: OrganizationFormData = {
  fullName: '',
  shortName: '',
  type: '',
  country: 'US',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  latitude: '',
  longitude: '',
  dioceseId: '',
  phone: '',
  website: '',
  timezone: '',
  isClusterEditable: false,
  clusterEnabled: false,
  clusterName: '',
  keyword: '',
  qbAccount: '',
  discoverMassId: '',
  clusterOrganizations: [],
};

const parseOptionalCoordinate = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return null;

  return parsed;
};

const formatCoordinate = (value?: number | null): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

type UseOrganizationFormOptions = {
  organization: Church | null;
  opened: boolean;
  mode: ORGANIZATION_MODAL_MODE;
  onSave?: (data: ChurchUpdateRequest) => Promise<void> | void;
  onCreate?: (data: ChurchCreateRequest) => Promise<void> | void;
  onClose: () => void;
  myParishData?: MyParishView | null;
  onSaveBulletin?: (data: MyParishBulletinUpdateRequest) => Promise<void> | void;
};

export function useOrganizationForm({
  organization,
  opened,
  mode,
  onSave,
  onCreate,
  onClose,
  myParishData,
  onSaveBulletin,
}: UseOrganizationFormOptions) {
  const [formData, setFormData] = useState<OrganizationFormData>(INITIAL_FORM_DATA);

  const [bulletinType, setBulletinType] = useState<BULLETIN_TYPE>(BULLETIN_TYPE.NONE);
  const [bulletinNumber, setBulletinNumber] = useState('');
  const [bulletinNumberError, setBulletinNumberError] = useState<string | null>(null);
  const [latitudeError, setLatitudeError] = useState<string | null>(null);
  const [longitudeError, setLongitudeError] = useState<string | null>(null);

  const [stateSearch, setStateSearch] = useState('');
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  const isCreateMode = mode === ORGANIZATION_MODAL_MODE.CREATE;
  const isDiocese = formData.type === CHURCH_TYPE.DIOCESE;

  const statesData = useMemo(() => STATES_DATA[formData.country as keyof typeof STATES_DATA] || [], [formData.country]);

  const stateCombobox = useCombobox({
    onDropdownClose: () => stateCombobox.resetSelectedOption(),
  });

  const timezoneCombobox = useCombobox({
    onDropdownClose: () => timezoneCombobox.resetSelectedOption(),
  });

  const dioceseSearch = useComboboxSearch({
    fetchFn: (query: string) => churchesService.getDioceses({ term: query }),
    mapFn: (church) => ({ value: church.id, label: church.name }),
  });

  const clusterSearch = useComboboxSearch({
    fetchFn: (query: string) => churchesService.getClusters({ term: query }),
    mapFn: (church) => ({
      value: church.id,
      label: church.name,
      city: church.city || '',
      zipCode: church.zipCode || '',
    }),
  });

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setStateSearch('');
    setTimezoneSearch('');
    dioceseSearch.setSearchTerm('');
    clusterSearch.setSearchTerm('');
    clusterSearch.setResults([]);
    setBulletinType(BULLETIN_TYPE.NONE);
    setBulletinNumber('');
    setBulletinNumberError(null);
    setLatitudeError(null);
    setLongitudeError(null);
    setSaveError(null);
  };

  const resetFormToOrganization = (org: Church) => {
    const countryValue = transformCountryLabelToValue(org.country || '') || 'US';
    const timezoneValue = org.timezone || '';

    setFormData({
      fullName: org.fullName || '',
      shortName: org.shortName || '',
      type: org.type || '',
      country: countryValue,
      addressLine1: org.addressLine1 || '',
      addressLine2: org.addressLine2 || '',
      city: org.city || '',
      state: org.state || '',
      zipCode: org.zipCode || '',
      latitude: formatCoordinate(org.latitude),
      longitude: formatCoordinate(org.longitude),
      dioceseId: org.diocese?.id || '',
      phone: (() => {
        if (!org.phone) return '';
        const raw = typeof org.phone === 'string' ? org.phone : `${org.phone.prefix || ''}${org.phone.number || ''}`;
        const server = toServerPhoneNumber(raw);
        return isPhoneValueEmpty(server) ? '' : server;
      })(),
      website: org.website || '',
      timezone: timezoneValue,
      isClusterEditable: org.isClusterEditable || false,
      clusterEnabled: !!org.cluster?.name || false,
      clusterName: org.cluster?.name || '',
      keyword: org.keyword || '',
      qbAccount: org.qbAccount || '',
      discoverMassId: org.discoverMassId || '',
      clusterOrganizations:
        org.cluster?.churches?.map((church) => ({
          id: church.id || '',
          name: church.name || '',
          city: church.city || '',
          zipCode: church.zipCode || '',
          productsEnabled: church.productsEnabled ?? false,
        })) || [],
    });

    const timezoneLabel = TIMEZONES.find((item) => item.value === timezoneValue)?.label || '';

    setStateSearch(org.state || '');
    setTimezoneSearch(timezoneLabel);
    dioceseSearch.setSearchTerm(org.diocese?.name || '');
    clusterSearch.setSearchTerm('');
    setLatitudeError(null);
    setLongitudeError(null);
  };

  useEffect(() => {
    if (!opened) return;

    if (isCreateMode) {
      resetForm();
      return;
    }

    if (organization) {
      resetFormToOrganization(organization);
    }
  }, [organization, opened, isCreateMode]);

  useEffect(() => {
    if (opened && myParishData) {
      setBulletinType(myParishData.bulletin?.type || BULLETIN_TYPE.NONE);
      setBulletinNumber(myParishData.bulletin?.number || '');
      setBulletinNumberError(null);
    }
  }, [opened, myParishData]);

  const handleInputChange = (
    field: keyof OrganizationFormData,
    value: string | boolean | ClusterOrganizationEntry[]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value: string) => {
    setFormData((prev) => ({ ...prev, country: value || 'US', state: '' }));
    setStateSearch('');
  };

  const handlePhoneChange = (value: string) => {
    const server = toServerPhoneNumber(value);
    setFormData((prev) => ({ ...prev, phone: isPhoneValueEmpty(server) ? '' : server }));
  };

  const handleRemoveClusterOrganization = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      clusterOrganizations: prev.clusterOrganizations.filter((org) => org.id !== id),
    }));
  };

  const handleClusterSelection = (value: string | null) => {
    if (!value) return;
    const selected = clusterSearch.results.find((c) => c.value === value);
    if (!selected) return;

    setFormData((prev) => {
      const isAlreadyAdded = prev.clusterOrganizations.some((org) => org.id === selected.value);
      if (isAlreadyAdded) return prev;

      return {
        ...prev,
        clusterOrganizations: [
          ...prev.clusterOrganizations,
          {
            id: selected.value,
            name: selected.label,
            city: selected.city,
            zipCode: selected.zipCode,
            productsEnabled: false,
          },
        ],
      };
    });
    clusterSearch.setSearchTerm('');
    clusterSearch.setResults([]);
  };

  const handleClusterProductsChange = (id: string, productsEnabled: boolean) => {
    setFormData((prev) => ({
      ...prev,
      clusterOrganizations: prev.clusterOrganizations.map((org) =>
        org.id === id ? { ...org, productsEnabled } : org
      ),
    }));
  };

  const handleBulletinTypeChange = (value: string | null) => {
    const nextType = (value as BULLETIN_TYPE) || BULLETIN_TYPE.NONE;
    setBulletinType(nextType);
    setBulletinNumberError(validateBulletinNumber(nextType, bulletinNumber));
  };

  const handleBulletinNumberChange = (value: string) => {
    setBulletinNumber(value);
    setBulletinNumberError(validateBulletinNumber(bulletinType, value));
  };

  const handleCoordinateChange = ({
    field,
    value,
  }: {
    field: COORDINATE_TYPE;
    value: string | number | bigint;
  }) => {
    const nextValue = value === '' ? '' : String(value);
    const nextLatitude = field === COORDINATE_TYPE.LATITUDE ? nextValue : formData.latitude;
    const nextLongitude = field === COORDINATE_TYPE.LONGITUDE ? nextValue : formData.longitude;

    setFormData((prev) => ({ ...prev, [field]: nextValue }));

    const { latitudeError: nextLatitudeError, longitudeError: nextLongitudeError } = validateCoordinatePair({
      latitude: nextLatitude,
      longitude: nextLongitude,
    });
    setLatitudeError(nextLatitudeError);
    setLongitudeError(nextLongitudeError);
  };

  const handleSave = async () => {
    const { latitudeError: currentLatitudeError, longitudeError: currentLongitudeError } = validateCoordinatePair({
      latitude: formData.latitude,
      longitude: formData.longitude,
    });
    setLatitudeError(currentLatitudeError);
    setLongitudeError(currentLongitudeError);
    if (currentLatitudeError || currentLongitudeError) return;

    if (!isCreateMode) {
      const currentBulletinError = validateBulletinNumber(bulletinType, bulletinNumber);
      setBulletinNumberError(currentBulletinError);
      if (currentBulletinError) return;
    }

    const normalizedPhone = isPhoneValueEmpty(formData.phone) ? '' : formData.phone.trim();

    const buildClusterPayload = () => {
      if (isDiocese) return undefined;

      if (formData.clusterEnabled && formData.clusterName) {
        return {
          id: organization?.cluster?.id || undefined,
          name: formData.clusterName,
          churches: formData.clusterOrganizations.map((org) => org.id),
          subChurchProducts: Object.fromEntries(
            formData.clusterOrganizations.map((org) => [org.id, org.productsEnabled])
          ),
        };
      }

      if (!isCreateMode && !formData.clusterEnabled && organization?.cluster?.name) {
        return null;
      }

      return undefined;
    };

    setIsSaving(true);
    setSaveError(null);

    try {
      if (isCreateMode) {
        const createPayload: ChurchCreateRequest = {
          fullName: formData.fullName.trim(),
          shortName: formData.shortName.trim(),
          country: formData.country,
          city: formData.city.trim(),
          state: formData.state,
          zipCode: formData.zipCode.trim(),
          addressLine1: formData.addressLine1.trim(),
          type: formData.type,
          keyword: formData.keyword.trim(),
          timezone: formData.timezone,
          phone: normalizedPhone,
          addressLine2: formData.addressLine2.trim() || null,
          latitude: parseOptionalCoordinate(formData.latitude),
          longitude: parseOptionalCoordinate(formData.longitude),
          dioceseId: !isDiocese && formData.dioceseId ? formData.dioceseId : null,
          website: formData.website.trim() || null,
          qbAccount: formData.qbAccount.trim() || null,
          discoverMassId: formData.discoverMassId.trim() || null,
          cluster: buildClusterPayload() ?? null,
        };

        await onCreate?.(createPayload);
        return;
      }

      const updatedOrganization: ChurchUpdateRequest = {
        fullName: formData.fullName,
        shortName: formData.shortName,
        country: formData.country,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2 || undefined,
        latitude: parseOptionalCoordinate(formData.latitude),
        longitude: parseOptionalCoordinate(formData.longitude),
        type: formData.type,
        timezone: formData.timezone,
        dioceseId: !isDiocese && formData.dioceseId ? formData.dioceseId : undefined,
        phone: normalizedPhone || null,
        website: formData.website || undefined,
        cluster: buildClusterPayload(),
        keyword: formData.keyword || undefined,
        qbAccount: formData.qbAccount.trim() || null,
        discoverMassId: formData.discoverMassId || undefined,
      };

      await onSave?.(updatedOrganization);

      if (onSaveBulletin && myParishData) {
        const trimmedBulletinNumber = bulletinNumber.trim();
        const originalBulletinType = myParishData.bulletin?.type || BULLETIN_TYPE.NONE;
        const originalBulletinNumber = myParishData.bulletin?.number || '';
        const hasBulletinChanged =
          bulletinType !== originalBulletinType ||
          (bulletinType !== BULLETIN_TYPE.NONE && trimmedBulletinNumber !== originalBulletinNumber);

        if (hasBulletinChanged) {
          await onSaveBulletin({
            type: bulletinType,
            number: bulletinType === BULLETIN_TYPE.NONE ? undefined : trimmedBulletinNumber || undefined,
          });
        }
      }
    } catch (error) {
      console.error('Failed to save organization:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      resetForm();
    } else if (organization) {
      resetFormToOrganization(organization);
    }
    onClose();
  };

  const isDiscoverMassIdValid = () => {
    const trimmedDiscoverMassId = formData.discoverMassId.trim();
    if (!trimmedDiscoverMassId) return true;

    return (
      trimmedDiscoverMassId.length >= DISCOVER_MASS_ID_MIN_LENGTH &&
      trimmedDiscoverMassId.length <= DISCOVER_MASS_ID_MAX_LENGTH
    );
  };

  const isFormValid = () => {
    const hasRequiredName = !!formData.fullName.trim() && !!formData.shortName.trim();
    const coordinateErrors = validateCoordinatePair({
      latitude: formData.latitude,
      longitude: formData.longitude,
    });
    const hasValidCoordinates = !coordinateErrors.latitudeError && !coordinateErrors.longitudeError;

    const hasRequiredBasics =
      hasRequiredName &&
      !!formData.type &&
      !!formData.country &&
      !!formData.addressLine1.trim() &&
      !!formData.city.trim() &&
      !!formData.state &&
      !!formData.zipCode.trim() &&
      (!!formData.dioceseId || isDiocese) &&
      isCompleteUsPhoneNumber(formData.phone) &&
      !!formData.timezone &&
      !!formData.keyword.trim() &&
      formData.keyword.length >= KEYWORD_MIN_LENGTH &&
      formData.keyword.length <= KEYWORD_MAX_LENGTH &&
      isDiscoverMassIdValid() &&
      hasValidCoordinates;

    const isClusterValid =
      !formData.clusterEnabled ||
      (formData.clusterEnabled && !!formData.clusterName && formData.clusterOrganizations.length > 0);

    return hasRequiredBasics && (isDiocese || isClusterValid);
  };

  return {
    formData,
    isCreateMode,
    isDiocese,
    statesData,
    stateSearch,
    setStateSearch,
    timezoneSearch,
    setTimezoneSearch,
    stateCombobox,
    timezoneCombobox,
    dioceseSearch,
    clusterSearch,

    bulletinType,
    bulletinNumber,
    bulletinNumberError,
    latitudeError,
    longitudeError,

    isSaving,
    saveError,

    handleInputChange,
    handleCountryChange,
    handlePhoneChange,
    handleRemoveClusterOrganization,
    handleClusterSelection,
    handleClusterProductsChange,
    handleBulletinTypeChange,
    handleBulletinNumberChange,
    handleCoordinateChange,
    handleSave,
    handleCancel,
    isFormValid,
  };
}
